import { motion } from 'framer-motion'
import React from 'react'

import { LittleText } from '@/components/common/UI/LittleText'
import { Icons } from '@/components/icons'
import { Tooltip } from '@/components/nextui'

interface StepProps {
    title: string
    id: number
    isCompleted: boolean
    isActive?: boolean
    isLast?: boolean
}
const Step: React.FC<StepProps> = ({
    title,
    isActive,
    isCompleted,
    id,
    isLast
}) => {
    const normalizedTitle = title
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

    const isProjection = normalizedTitle === 'proyeccion'

    const bgColor =
        isActive && !isCompleted
            ? 'bg-warning'
            : isCompleted
              ? `${isProjection ? 'bg-success' : 'bg-primary'} text-white`
              : 'bg-transparent'

    const setIcon = (title: string): JSX.Element => {
        switch (title) {
            case 'Bienvenida':
                return <Icons.Home size={21} />
            case 'Conozcámonos':
                return <Icons.People size={21} />
            case 'Cuéntanos sobre ti':
                return <Icons.UserCheck size={21} />
            case 'Sube tus documentos':
                return <Icons.FileCheck size={21} />
            case 'Entrevista':
                return <Icons.Interview size={21} />
            case 'Proyección':
                return <Icons.File size={21} />
            case 'Pago':
                return <Icons.Economy size={21} />
            default:
                return <div></div>
        }
    }

    return (
        <div className={'flex  items-center'}>
            <div className="flex flex-col items-center h-16">
                <motion.div
                    className={`flex justify-center items-center w-8 h-8 rounded-full ${bgColor}`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                >
                    {setIcon(title)}
                </motion.div>
                <motion.div
                    className={`text-xs font-light text-center text-foreground`}
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                >
                    <LittleText text={title} />
                </motion.div>
            </div>
            {!isLast && <Icons.Minus className="w-3 text-default-500 m-2" />}
        </div>
    )
}

interface StepsProps {
    activeStep: number
    onStepChange: (step: number) => void
    /**
     * Inscripción seleccionada inactiva: deshabilitar clic en pasos que no aplican
     * (Cuéntanos sobre ti, Sube tus documentos, Entrevista, Proyección, Pago).
     */
    isInactiveInscription?: boolean
    children: React.ReactElement[]
}

type StepsCompound = React.FC<StepsProps> & { Step: typeof Step }

function isInactiveInscriptionStepBlocked(
    stepTitle: string,
    isInactiveInscription: boolean
): boolean {
    if (!isInactiveInscription) {
        return false
    }

    return (
        stepTitle === 'Sube tus documentos' ||
        stepTitle === 'Entrevista' ||
        stepTitle === 'Proyección' ||
        stepTitle === 'Pago'
    )
}

function isSequentialFlowStepBlocked(params: {
    stepTitle: string
    stepCompleted: boolean
    isBienvenidaCompleted: boolean
    isConozcamonosCompleted: boolean
    isInactiveInscription: boolean
}): boolean {
    const {
        stepTitle,
        stepCompleted: isCompleted,
        isBienvenidaCompleted,
        isConozcamonosCompleted,
        isInactiveInscription
    } = params

    if (stepTitle === 'Conozcámonos' && !isBienvenidaCompleted) {
        return true
    }

    if (
        stepTitle === 'Cuéntanos sobre ti' &&
        (!isBienvenidaCompleted || isCompleted || isInactiveInscription)
    ) {
        return true
    }

    return (
        stepTitle === 'Sube tus documentos' &&
        (!isBienvenidaCompleted || !isConozcamonosCompleted)
    )
}

function isStepClickDisabled(params: {
    stepTitle: string
    stepCompleted: boolean
    isBienvenidaCompleted: boolean
    isConozcamonosCompleted: boolean
    isInactiveInscription: boolean
}): boolean {
    const {
        stepTitle,
        stepCompleted,
        isBienvenidaCompleted,
        isConozcamonosCompleted,
        isInactiveInscription
    } = params

    if (
        isSequentialFlowStepBlocked({
            stepTitle,
            stepCompleted,
            isBienvenidaCompleted,
            isConozcamonosCompleted,
            isInactiveInscription
        })
    ) {
        return true
    }

    if (isInactiveInscriptionStepBlocked(stepTitle, isInactiveInscription)) {
        return true
    }

    if (stepTitle === 'Entrevista' && stepCompleted) {
        return true
    }

    return false
}

const getDisableReason = (
    stepTitle: string,
    isBienvenidaCompleted: boolean,
    isConozcamonosCompleted: boolean,
    isCompleted: boolean
): string => {
    if (stepTitle === 'Conozcámonos' && !isBienvenidaCompleted) {
        return 'Debes completar Bienvenida primero.'
    }
    if (stepTitle === 'Sube tus documentos') {
        if (!isBienvenidaCompleted) {
            return 'Debes completar Bienvenida primero.'
        }
        if (!isConozcamonosCompleted) {
            return 'Debes completar Conozcámonos antes de subir tus documentos.'
        }
    }
    if (stepTitle === 'Cuéntanos sobre ti') {
        if (!isBienvenidaCompleted) {
            return 'Debes completar Bienvenida primero.'
        }
        if (isCompleted) {
            return 'Ya completaste este paso.'
        }
    }
    if (stepTitle === 'Entrevista' && isCompleted) {
        return 'Ya completaste este paso.'
    }

    return ''
}

const computeShouldDisable = (
    stepTitle: string,
    isBienvenidaCompleted: boolean,
    isConozcamonosCompleted: boolean,
    isCompleted: boolean
): boolean => {
    if (stepTitle === 'Conozcámonos') {
        return !isBienvenidaCompleted
    }
    if (stepTitle === 'Cuéntanos sobre ti') {
        return !isBienvenidaCompleted || isCompleted
    }
    if (stepTitle === 'Sube tus documentos') {
        return !isBienvenidaCompleted || !isConozcamonosCompleted
    }
    if (stepTitle === 'Entrevista') {
        return isCompleted
    }

    return false
}

interface StepItemProps {
    child: React.ReactElement
    index: number
    activeStep: number
    totalSteps: number
    isBienvenidaCompleted: boolean
    isConozcamonosCompleted: boolean
    isInactiveInscription: boolean
    onStepChange: (step: number) => void
}

const StepItem: React.FC<StepItemProps> = ({
    child,
    index,
    activeStep,
    totalSteps,
    isBienvenidaCompleted,
    isConozcamonosCompleted,
    isInactiveInscription,
    onStepChange
}) => {
    const isCompleted: boolean = child.props.isCompleted
    const stepTitle: string = child.props.title

    const shouldDisable = isStepClickDisabled({
        stepTitle,
        stepCompleted: isCompleted,
        isBienvenidaCompleted,
        isConozcamonosCompleted,
        isInactiveInscription
    })

    const disableReason = shouldDisable
        ? getDisableReason(
              stepTitle,
              isBienvenidaCompleted,
              isConozcamonosCompleted,
              isCompleted
          )
        : ''

    const stepNode = (
        <div className="flex items-center gap-2">
            <div
                onClick={() => {
                    if (!shouldDisable) {
                        onStepChange(index)
                    }
                }}
                className={`${
                    shouldDisable
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-pointer'
                }`}
            >
                {React.cloneElement(child, {
                    isActive: index === activeStep,
                    isLast: index === totalSteps - 1
                })}
            </div>
        </div>
    )

    if (shouldDisable && disableReason) {
        return (
            <Tooltip
                content={disableReason}
                placement="bottom"
                color="foreground"
            >
                {stepNode}
            </Tooltip>
        )
    }

    return stepNode
}

const StepsBase: React.FC<StepsProps> = ({
    activeStep,
    onStepChange,
    isInactiveInscription = false,
    children
}) => {
    const hasBienvenida = children.some((c) => c.props.title === 'Bienvenida')
    const isBienvenidaCompleted = hasBienvenida
        ? (children.find((c) => c.props.title === 'Bienvenida')?.props
              .isCompleted ?? false)
        : true
    const isConozcamonosCompleted =
        children.find((c) => c.props.title === 'Conozcámonos')?.props
            .isCompleted || false

    return (
        <div className="pb-1">
            <div className="flex items-center gap-2">
                {React.Children.map(children, (child, index) => (
                    <StepItem
                        key={index}
                        child={child}
                        index={index}
                        activeStep={activeStep}
                        totalSteps={React.Children.count(children)}
                        isBienvenidaCompleted={isBienvenidaCompleted}
                        isConozcamonosCompleted={isConozcamonosCompleted}
                        isInactiveInscription={isInactiveInscription}
                        onStepChange={onStepChange}
                    />
                ))}
            </div>
        </div>
    )
}

export const Steps = Object.assign(StepsBase, { Step }) as StepsCompound
